import { request } from './http'
import type { Asset, AssetLibraryFilter, AssetLibraryItem, ConceptAssistResponse, Modalities, Project } from '@/types/nf'
import type { CascadePolicy, UpdateCascadePolicyInput } from '@/types/pulse'

interface ProjectListResponse {
  projects: Project[] | null
}

interface ProjectTrashResponse {
  projects: Project[] | null
}

interface ProjectAssetsResponse {
  assets: Asset[] | null
}

interface AssetLibraryResponse {
  items: AssetLibraryItem[] | null
}

export interface CreateProjectInput {
	 genre_label_custom?: string
  user_id: string
  title: string
  description?: string
  genre?: string
  theme?: string
  target_words?: number
  core_theme?: string
  content_type?: string
  length_form?: string
  secondary_drivers?: string[]
  logline?: string
  antagonist_source?: string
  tone_keynote?: string
  content_language?: string
  narrative_perspective?: string
  cascade_auto_threshold?: 'passed_only' | 'review_ok' | 'manual'
  cascade_stop_at?: 'text' | 'screenplay' | 'storyboard' | 'image' | 'audio' | 'video'
}

export interface ConceptAssistInput {
  field: 'core_theme' | 'logline' | 'project_draft' | 'chapter_draft'
  idea?: string
  length_form?: string
  existing_chapter_count?: number
  genre_key?: string
  genre_label?: string
  content_language?: string
  secondary_drivers?: string[]
  logline?: string
  golden_finger?: string
  selling_point?: string
  protagonist?: {
    name?: string
    motivation?: string
    inner_flaw?: string
    identity_situation?: string
  }
  world?: {
    time_period?: string
    location?: string
    atmosphere?: string
  }
  use_llm?: boolean
}

export interface SecondaryDriverOption {
  key: string
  labels: Record<string, string>
}

export interface ProjectOptions {
  secondary_drivers: SecondaryDriverOption[]
}

export interface PatchProjectInput {
  author?: string
  budget_credits?: number | null
  title?: string
  description?: string
  status?: string
  world_time_period?: string
  world_location?: string
  world_atmosphere?: string
  world_rules?: string
  core_theme?: string
  logline?: string
  antagonist_source?: string
  tone_keynote?: string
  narrative_perspective?: string
  content_language?: string
}

export interface SoftConsistencySetting {
  value: boolean | null
  effective: boolean
}

export interface SoftConsistencyDimension extends SoftConsistencySetting {
  experimental: boolean
}

export interface SoftConsistencyStatus {
  globally_available: boolean
  master: SoftConsistencySetting
  dimensions: {
    ooc: SoftConsistencyDimension
    setting: SoftConsistencyDimension
    emotion: SoftConsistencyDimension
  }
}

export interface UpdateSoftConsistencyInput {
  enabled?: boolean
  ooc_enabled?: boolean
  setting_enabled?: boolean
  emotion_enabled?: boolean
}

export interface ProjectSkillSetting {
  name: string
  description: string
  category: string
  source: string
  genre?: string
  nf_stage?: string
  enabled: boolean
}

export interface ProjectSkillsStatus {
  skills: ProjectSkillSetting[]
}

export interface UpdateProjectSkillInput {
  enabled: boolean
}

export interface ProjectAct {
  act_no: number
  start_chapter_no: number
  end_chapter_no: number
  midpoint_chapter_no: number | null
  beat_role: string
  enabled: boolean
}

export interface ProjectActsResponse {
  project_id: string
  branch_id: string
  persisted: boolean
  acts: ProjectAct[]
}

export interface UpdateProjectActsInput {
  acts: ProjectAct[]
}

export interface CanonicalString {
  value: string
  strictness?: 'exact' | 'semantic' | 'semantic-soft' | 'loose' | 'ordinal' | string
  source?: string
}

export interface CanonicalSceneSpec {
  scene_id: string
  setting?: CanonicalString
  intent?: CanonicalString
  emotion?: CanonicalString[]
  props?: CanonicalString[]
  lighting?: CanonicalString
  weather?: CanonicalString
  present_characters?: CanonicalString[]
}

export interface SceneLock {
  project_id: string
  scene_id: string
  spec: CanonicalSceneSpec
  override_reason?: string
  created_at?: number
  updated_at?: number
}

export interface SceneLocksResponse {
  scene_locks: SceneLock[]
}

export const projectApi = {
  getCascadeEstimate: (id: string, stopAt: string): Promise<{ estimated_tokens: number | null; chapter_count: number; stop_at: string; stages: Array<{ stage: string; average_tokens: number | null; samples: number }> }> =>
    request(`/v1/projects/${encodeURIComponent(id)}/cascade-estimate?stop_at=${encodeURIComponent(stopAt)}`),
  list: async (userId: string): Promise<Project[]> => {
    const qs = new URLSearchParams({ user_id: userId })
    const data = await request<ProjectListResponse>(`/v1/projects?${qs.toString()}`)
    return data.projects ?? []
  },

  create: (body: CreateProjectInput): Promise<Project> =>
    request<Project>('/v1/projects', { method: 'POST', body }),

  options: (): Promise<ProjectOptions> =>
    request<ProjectOptions>('/v1/projects/options'),

  conceptAssist: (body: ConceptAssistInput): Promise<ConceptAssistResponse> =>
    request<ConceptAssistResponse>('/v1/projects/concept-assist', { method: 'POST', body }),

  get: (id: string): Promise<Project> => request<Project>(`/v1/projects/${encodeURIComponent(id)}`),

  patch: (id: string, body: PatchProjectInput): Promise<Project> =>
    request<Project>(`/v1/projects/${encodeURIComponent(id)}`, { method: 'PATCH', body }),

  delete: (id: string): Promise<void> =>
    request<void>(`/v1/projects/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  trash: async (userId: string): Promise<Project[]> => {
    const qs = new URLSearchParams({ user_id: userId })
    const data = await request<ProjectTrashResponse>(`/v1/projects/trash?${qs.toString()}`)
    return data.projects ?? []
  },

  restore: (id: string): Promise<Project> =>
    request<Project>(`/v1/projects/${encodeURIComponent(id)}/restore`, { method: 'POST' }),

  purge: (id: string): Promise<void> =>
    request<void>(`/v1/projects/${encodeURIComponent(id)}/purge`, { method: 'POST' }),

  getModalities: (id: string): Promise<Modalities> =>
    request<Modalities>(`/v1/projects/${encodeURIComponent(id)}/modalities`),

  putModalities: (id: string, mods: Modalities): Promise<Modalities> =>
    request<Modalities>(`/v1/projects/${encodeURIComponent(id)}/modalities`, { method: 'PUT', body: mods }),

  getSoftConsistency: (id: string): Promise<SoftConsistencyStatus> =>
    request<SoftConsistencyStatus>(`/v1/projects/${encodeURIComponent(id)}/soft-consistency`),

  updateSoftConsistency: (id: string, body: UpdateSoftConsistencyInput): Promise<SoftConsistencyStatus> =>
    request<SoftConsistencyStatus>(`/v1/projects/${encodeURIComponent(id)}/soft-consistency`, { method: 'POST', body }),

  getProjectSkills: (id: string): Promise<ProjectSkillsStatus> =>
    request<ProjectSkillsStatus>(`/v1/projects/${encodeURIComponent(id)}/skills`),

  updateProjectSkillEnabled: (id: string, name: string, body: UpdateProjectSkillInput): Promise<ProjectSkillSetting> =>
    request<ProjectSkillSetting>(`/v1/projects/${encodeURIComponent(id)}/skills/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body,
    }),

  listSceneLocks: async (id: string): Promise<SceneLock[]> => {
    const data = await request<SceneLocksResponse>(`/v1/projects/${encodeURIComponent(id)}/scene-locks`)
    return data.scene_locks ?? []
  },

  putSceneLock: (
    id: string,
    sceneId: string,
    body: { spec: CanonicalSceneSpec; override_reason?: string },
  ): Promise<SceneLock> =>
    request<SceneLock>(`/v1/projects/${encodeURIComponent(id)}/scene-locks/${encodeURIComponent(sceneId)}`, {
      method: 'PUT',
      body,
    }),

  getCascadePolicy: (id: string): Promise<CascadePolicy> =>
    request<CascadePolicy>(`/v1/projects/${encodeURIComponent(id)}/cascade-policy`),

  putCascadePolicy: (id: string, body: UpdateCascadePolicyInput): Promise<CascadePolicy> =>
    request<CascadePolicy>(`/v1/projects/${encodeURIComponent(id)}/cascade-policy`, { method: 'PUT', body }),

  getActs: (id: string, branchId?: string | null): Promise<ProjectActsResponse> =>
    request<ProjectActsResponse>(`/v1/projects/${encodeURIComponent(id)}/acts`, { withBranch: true, branchId }),

  putActs: (id: string, body: UpdateProjectActsInput, branchId?: string | null): Promise<ProjectActsResponse> => {
    const qs = new URLSearchParams()
    if (branchId) qs.set('branch_id', branchId)
    const suffix = qs.size > 0 ? `?${qs.toString()}` : ''
    return request<ProjectActsResponse>(`/v1/projects/${encodeURIComponent(id)}/acts${suffix}`, { method: 'PUT', body })
  },

  assets: async (id: string): Promise<Asset[]> => {
    const data = await request<ProjectAssetsResponse>(`/v1/projects/${encodeURIComponent(id)}/assets`)
    return data.assets ?? []
  },

  assetLibrary: async (
    id: string,
    options: { branchId?: string; type?: AssetLibraryFilter } = {},
  ): Promise<AssetLibraryItem[]> => {
    const qs = new URLSearchParams()
    if (options.branchId) qs.set('branch_id', options.branchId)
    if (options.type && options.type !== 'all') qs.set('type', options.type)
    const suffix = qs.size > 0 ? `?${qs.toString()}` : ''
    const data = await request<AssetLibraryResponse>(`/v1/projects/${encodeURIComponent(id)}/asset-library${suffix}`)
    return data.items ?? []
  },
}
